import { getDoseComponents } from "@/lib/dose-components";
import { pickMedicationByName } from "@/lib/medication-select";

import { EventStatus, EventType } from "@/types/enums/event";
import { Unit } from "@/types/enums/unit";
import { ORAL_MEDICATION_NAMES } from "@/types/enums/weight-loss-medications";
import { EventDetail } from "@/types/event";
import {
  DoseComponent,
  Medication,
  MedicationCreate,
  MedicationSummary,
} from "@/types/medication";
import { NoteDetail, NoteListItem } from "@/types/notes";

import { expandCompoundComponents } from "./compound-expand";
import {
  DoseComponentFormValues,
  DoseFormValues,
  componentsAreOral,
  emptyDoseComponent,
} from "./dose-form-schema";
import {
  DoseLotInventory,
  serializeDoseAllocations,
} from "./dose-lot-resolution";

const toLotSelection = (lotNumber: string | null | undefined) =>
  lotNumber ? ({ type: "lot", value: lotNumber } as const) : null;

const componentFromDoseComponent = (
  component: DoseComponent,
): DoseComponentFormValues => ({
  medicationId: component.medicationId,
  name: component.name || "",
  amount: component.amount || 0,
  unit: (component.unit as Unit) || Unit.MG,
  lotSelection: null,
  quantity: component.quantity || 1,
});

const componentFromSavedMedication = (
  name: string,
  savedMedication: Medication,
): DoseComponentFormValues => ({
  ...emptyDoseComponent(name),
  medicationId: savedMedication.id,
  unit: (savedMedication.unit as Unit) || Unit.MG,
});

/**
 * Re-anchor a prefilled component to a row that is actually in the active catalog.
 *
 * Prefills copy the medication name off a previous dose, so a medication archived since
 * that dose leaves a name the picker no longer offers. Blank the identity in that case:
 * the row then reads as unselected and the user has to pick, instead of submitting a
 * name-only component that the API would turn into a brand-new catalog entry.
 *
 * An empty catalog means "still loading", not "nothing matches" — leave the prefill alone
 * there, or every row would blank on first render and only recover if the form is still
 * pristine when the list lands.
 */
const withCatalogIdentity = (
  component: DoseComponentFormValues,
  medications: Medication[],
): DoseComponentFormValues => {
  if (medications.length === 0) return component;

  const linked = component.medicationId
    ? medications.find((medication) => medication.id === component.medicationId)
    : undefined;
  const resolved = linked ?? resolveCreateMedication(component, medications);

  if (!resolved) {
    return { ...component, medicationId: undefined, name: "" };
  }
  return { ...component, medicationId: resolved.id, name: resolved.name };
};

export interface CreateDoseMedicationDefaults {
  medication?: Medication;
  component: DoseComponentFormValues;
  eventFields: Pick<
    DoseFormValues,
    "administrationLocation" | "comment" | "frequency"
  >;
}

export function getCreateDoseMedicationDefaults(
  name: string,
  medications: Medication[],
): CreateDoseMedicationDefaults {
  const medication = pickMedicationByName(medications, name);
  const template = medication?.template;

  if (medication && template) {
    return {
      medication,
      component: {
        medicationId: medication.id,
        name,
        amount: template.amount ?? 1,
        unit:
          (template.unit as Unit | null) ||
          (medication.unit as Unit | null) ||
          Unit.MG,
        lotSelection: toLotSelection(template.lotNumber),
        quantity: template.quantity ?? 1,
      },
      eventFields: {
        administrationLocation: template.administrationLocation || "",
        comment: template.comment || "",
        frequency: template.frequency || "",
      },
    };
  }

  return {
    medication,
    component: medication
      ? componentFromSavedMedication(name, medication)
      : emptyDoseComponent(name),
    eventFields: {
      administrationLocation: "",
      comment: "",
      frequency: "",
    },
  };
}

interface CreateDoseInitialValuesOptions {
  selectedMedicationType?: string;
  mostRecentDose?: MedicationSummary | null;
  sourceDose?: MedicationSummary | null;
  medications: Medication[];
}

export function getCreateDoseFormValues({
  selectedMedicationType,
  mostRecentDose,
  sourceDose,
  medications,
}: CreateDoseInitialValuesOptions): DoseFormValues {
  if (sourceDose) {
    // Duplicating a compound dose re-expands from the compound's CURRENT recipe rather than
    // copying the historical components — the recipe may have drifted, and the create endpoint
    // validates the components against today's ingredients. If the source's compound is gone (or
    // no longer a compound), fall back to per-component copy and drop the stale compound link.
    const sourceCompound = sourceDose.compoundMedicationId
      ? medications.find(
          (m) => m.id === sourceDose.compoundMedicationId && m.isCompound,
        )
      : undefined;
    const compoundComponents = sourceCompound
      ? expandCompoundComponents(sourceCompound, medications)
      : null;
    return {
      components:
        compoundComponents && compoundComponents.length > 0
          ? compoundComponents
          : getDoseComponents(sourceDose).map((component) =>
              withCatalogIdentity(
                componentFromDoseComponent(component),
                medications,
              ),
            ),
      administrationLocation: sourceDose.administrationLocation || "",
      doseDate: new Date(),
      sideEffects: sourceDose.sideEffects || "",
      // A face chart's marker names the chart that owns the dose; a copy is not the chart's.
      comment: sourceDose.chartAttachmentId ? "" : sourceDose.comment || "",
      frequency: sourceDose.frequency || "",
      providerId: undefined,
      managementStatus: sourceDose.managementStatus ?? null,
      compoundMedicationId: sourceCompound?.id ?? null,
    };
  }

  // A selected compound always expands into its ingredient components and takes precedence over
  // the recent-dose prefill, so picking a compound never produces a non-expanded single line.
  const selectedCompound = selectedMedicationType
    ? pickMedicationByName(medications, selectedMedicationType)
    : undefined;
  if (selectedCompound?.isCompound) {
    const compoundComponents = expandCompoundComponents(
      selectedCompound,
      medications,
    );
    // A preselected compound must honor its template's administration defaults just like an
    // interactive pick does — the expanded components replace the template's single component.
    const { eventFields } = getCreateDoseMedicationDefaults(
      selectedMedicationType ?? "",
      medications,
    );
    return {
      components:
        compoundComponents.length > 0
          ? compoundComponents
          : [emptyDoseComponent(selectedMedicationType ?? "")],
      administrationLocation: eventFields.administrationLocation,
      doseDate: new Date(),
      sideEffects: "",
      comment: eventFields.comment,
      frequency: eventFields.frequency,
      providerId: undefined,
      managementStatus: null,
      compoundMedicationId: selectedCompound.id,
    };
  }

  if (mostRecentDose) {
    return {
      components: getDoseComponents(mostRecentDose).map((component, index) => {
        const prefilled = componentFromDoseComponent(component);
        // A caller-supplied medication type overrides the first row's medication, so the
        // id carried over from the previous dose no longer describes it.
        const overriddenName = index === 0 ? selectedMedicationType : undefined;
        return withCatalogIdentity(
          overriddenName
            ? { ...prefilled, medicationId: undefined, name: overriddenName }
            : prefilled,
          medications,
        );
      }),
      administrationLocation: mostRecentDose.administrationLocation || "",
      doseDate: new Date(),
      sideEffects: "",
      comment: "",
      frequency: mostRecentDose.frequency || "",
      providerId: undefined,
      managementStatus: null,
      compoundMedicationId: null,
    };
  }

  const medicationDefaults = selectedMedicationType
    ? getCreateDoseMedicationDefaults(selectedMedicationType, medications)
    : undefined;

  if (medicationDefaults?.medication && selectedMedicationType) {
    // A prefilled compound expands into its ingredient components so the dose is
    // recorded (and billed) as a compound, not a single line.
    const compound = medicationDefaults.medication.isCompound
      ? medicationDefaults.medication
      : null;
    const compoundComponents = compound
      ? expandCompoundComponents(compound, medications)
      : null;
    return {
      components:
        compoundComponents && compoundComponents.length > 0
          ? compoundComponents
          : [medicationDefaults.component],
      administrationLocation:
        medicationDefaults.eventFields.administrationLocation,
      doseDate: new Date(),
      sideEffects: "",
      comment: medicationDefaults.eventFields.comment,
      frequency: medicationDefaults.eventFields.frequency,
      providerId: undefined,
      managementStatus: null,
      compoundMedicationId: compound?.id ?? null,
    };
  }

  return {
    components: [emptyDoseComponent(selectedMedicationType || "")],
    administrationLocation: "",
    doseDate: new Date(),
    sideEffects: "",
    comment: "",
    frequency: "",
    providerId: undefined,
    managementStatus: null,
    compoundMedicationId: null,
  };
}

export function resolveCreateMedication(
  component: DoseComponentFormValues,
  medications: Medication[],
): Medication | undefined {
  return (
    medications.find(
      (medication) =>
        medication.name === component.name &&
        (!component.unit || medication.unit === component.unit),
    ) ?? medications.find((medication) => medication.name === component.name)
  );
}

/** How far back a patient's appointment can have started and still set the dose's location. */
const RECENT_APPOINTMENT_MS = 24 * 60 * 60 * 1000;

const SKIPPED_APPOINTMENT_STATUSES = new Set([
  EventStatus.CANCELLED,
  EventStatus.NO_SHOW,
]);

/**
 * Location of the patient's appointment that starts closest to `now`, counting
 * only appointments that started in the last 24 hours or start later today.
 * `endOfToday` is the end of the clinic's day, so tomorrow's visits never count.
 */
export function nearestAppointmentLocationId(
  appointments: EventDetail[],
  now: Date,
  endOfToday: Date,
): string | undefined {
  const earliest = now.getTime() - RECENT_APPOINTMENT_MS;
  let nearest: { locationId: string; distance: number } | undefined;
  for (const appointment of appointments) {
    const locationId = appointment.location?.id;
    if (
      !locationId ||
      appointment.type !== EventType.APPOINTMENT ||
      SKIPPED_APPOINTMENT_STATUSES.has(appointment.status)
    ) {
      continue;
    }
    const start = new Date(appointment.start).getTime();
    if (start < earliest || start >= endOfToday.getTime()) continue;
    const distance = Math.abs(start - now.getTime());
    if (!nearest || distance < nearest.distance) {
      nearest = { locationId, distance };
    }
  }
  return nearest?.locationId;
}

export interface CreateDoseInventorySnapshot extends DoseLotInventory {
  medicationId?: string;
}

const toComponentPayload = (
  component: DoseComponentFormValues,
  currentLocationId: string | null | undefined,
  inventory: CreateDoseInventorySnapshot | undefined,
): DoseComponent => {
  const isOral = ORAL_MEDICATION_NAMES.includes(component.name);
  const lotSelection = component.lotSelection;
  const shipmentAllocations = serializeDoseAllocations(
    component,
    inventory ?? { shipments: [] },
  );
  const untrackedLots = isOral
    ? []
    : (lotSelection?.value ?? "")
        .split("\n")
        .map((value) => value.trim())
        .filter(Boolean);

  return {
    // The form value is the id the user selected; the inventory snapshot is only a
    // name+unit re-lookup and misses whenever the catalog row was renamed or archived.
    medicationId: component.medicationId ?? inventory?.medicationId,
    name: component.name,
    amount: component.amount,
    unit: component.unit,
    quantity: component.quantity,
    ...(component.dilutionId !== undefined && {
      dilutionId: component.dilutionId,
    }),
    untrackedLots,
    shipmentAllocations,
    ...(!isOral &&
      component.clinicalShipmentIds !== undefined && {
        clinicalShipmentIds: component.clinicalShipmentIds,
      }),
    ...(component.lotAllocations !== undefined &&
      !isOral && { reuseCheckoutInventory: component.checkoutReuse === true }),
    locationId: currentLocationId || undefined,
  };
};

interface CreateDosePayloadOptions {
  patientId: string;
  selectedNote?: NoteListItem | NoteDetail | null;
  /** Appointment the dose is logged against. Falls back to the selected note's appointment. */
  eventId?: string | null;
  providerId: string;
  currentLocationId?: string | null;
  inventory: CreateDoseInventorySnapshot[];
}

export function toCreateDosePayload(
  values: DoseFormValues,
  {
    patientId,
    selectedNote,
    eventId,
    providerId,
    currentLocationId,
    inventory,
  }: CreateDosePayloadOptions,
): MedicationCreate {
  return {
    patientId,
    components: values.components.map((component, index) => {
      if (
        component.lotAllocations !== undefined &&
        (inventory[index]?.compoundMedicationId ?? null) !==
          (values.compoundMedicationId ?? null)
      )
        throw new Error(
          "Compound context changed. Review the dose inventory before saving.",
        );
      return toComponentPayload(component, currentLocationId, inventory[index]);
    }),
    administrationLocation: componentsAreOral(values.components)
      ? ""
      : values.administrationLocation,
    sideEffects: values.sideEffects,
    locationId: currentLocationId || undefined,
    noteId: selectedNote?.id,
    eventId: eventId ?? selectedNote?.eventId ?? undefined,
    providerId,
    doseDate: values.doseDate?.toISOString(),
    comment: values.comment,
    frequency: values.frequency,
    managementStatus: values.managementStatus,
    compoundMedicationId: values.compoundMedicationId ?? null,
  };
}
